import { useQuery } from '@tanstack/react-query';
import { Explicit } from '../../components/ui/Explicit';
import { DrawerLink } from '../../app/DrawerLink';
import { Link } from 'react-router-dom';

import { Card, Chip } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import { useFavorites } from '../../stores/favorites';
import type { TrackSummary } from '../tracks/TrackList';
import { useDocumentTitle } from '../../lib/title';

/**
 * Everything the signed-in user has favourited, newest first.
 *
 * ## Unfavouriting here does not remove the row
 *
 * Deliberate. A list that empties itself as you click through it makes it very
 * easy to lose something by accident and impossible to put back - the row you
 * would click to undo has already gone. So the heart empties and the track
 * stays until the page is next loaded, which is the behaviour every music
 * service settled on for the same reason.
 */
export function FavoritesPage() {
  useDocumentTitle('Favorites');

  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);
  const overlay = useFavorites((state) => state.overlay);

  const { data, isPending } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => api.get<{ items: TrackSummary[] }>('/me/favorites'),
  });

  if (isPending) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-2 px-6 py-10" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{copy['nav.favorites']}</h1>
      <p className="mt-1.5 text-sm text-soft">
        Everything you've favourited, newest first. Only you can see this.
      </p>

      {items.length === 0 ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-soft">
            Nothing yet. The heart on any track — including the one in the player bar — puts it here.
          </p>
        </Card>
      ) : (
        <ul className="mt-6 flex flex-col gap-2" data-favorites-list>
          {items.map((track) => {
            const isCurrent = current?.id === track.id;
            // Still in the list after unfavouriting; only the heart changes.
            const stillFavorited = overlay[track.id] ?? true;

            return (
              <li key={track.id} data-favorite-row={track.id}>
                <Card
                  className={`flex items-center gap-4 p-4 transition-opacity ${
                    stillFavorited ? '' : 'opacity-60'
                  }`}
                >
                  <button
                    type="button"
                    aria-label={isCurrent && playing ? `Pause ${track.title}` : `Play ${track.title}`}
                    onClick={() => (isCurrent ? toggle() : void play(track, items, 'profile'))}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink transition-colors hover:bg-accent-hover"
                  >
                    {isCurrent && playing ? (
                      <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <rect x="4" y="2.5" width="3" height="11" rx="1" />
                        <rect x="9" y="2.5" width="3" height="11" rx="1" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 16 16" className="h-4 w-4 translate-x-px" fill="currentColor" aria-hidden="true">
                        <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
                      </svg>
                    )}
                  </button>

                  <div className="min-w-0 flex-1">
                    <DrawerLink
                      to={`/tracks/${track.id}`}
                      className="block truncate text-sm font-medium text-ink hover:underline"
                    >
                      {track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
                      {track.title}
                    </DrawerLink>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {track.owner && (
                        <Link to={`/${track.owner.username}`} className="meta-caps hover:text-ink">
                          {track.owner.display_name || `@${track.owner.username}`}
                        </Link>
                      )}
                      <span className="meta-caps">{duration(track.duration_ms)}</span>
                      {!stillFavorited && <Chip>Removed</Chip>}
                    </div>
                  </div>

                  <FavoriteButton
                    trackId={track.id}
                    favorited={track.favorited}
                    likeCount={track.like_count}
                    size="sm"
                  />
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function duration(ms: number | null): string {
  if (ms === null) return '—';

  const total = Math.round(ms / 1000);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
